type TimelineEventLike = {
  id?: string | null;
  eventType?: string | null;
  actorName?: string | null;
  occurredAt?: string | null;
  metadata?: Record<string, unknown> | null;
  isCustomerVisible?: boolean;
};

type TimelineAudience = 'customer' | 'tech';

const CUSTOMER_EVENT_TYPES = new Set([
  'CASE_CREATED',
  'CASE_STATUS_UPDATED',
  'TASK_GROUP_ASSIGNED',
  'TASK_REASSIGNED',
  'CASE_SURVEY_RELEASED',
  'CASE_SURVEY_SUBMITTED',
  'SERVICE_REPORT_SHARED',
  'QUOTE_SHARED',
  'QUOTE_INACTIVATED',
  'QUOTE_REACTIVATED',
  'QUOTE_ACCEPTED',
  'QUOTE_DECLINED',
  'QUOTE_ADJUST_REQUESTED',
  'VISIT_SCHEDULED',
  'VISIT_RESCHEDULED',
  'VISIT_SCHEDULE_CONFIRMED',
  'VISIT_COMPLETED',
  'CASE_RESOLVED',
  'CASE_CLOSED'
]);

const TECH_EXTRA_EVENT_TYPES = new Set([
  'TASK_CREATED',
  'CASE_PROBLEM_UPDATED',
  'CASE_DOCUMENT_ATTACHED',
  'CASE_DEVICE_VERIFICATION_REQUIRED',
  'CASE_DEVICE_VERIFIED',
  'CASE_DEVICE_INSTALL_DATES_UPDATED',
  'TSFAD_ANALYSIS_ACCEPTED',
  'REPAIR_CUSTOMER_MESSAGE_SENT',
  'REPAIR_STOCK_CONSUMED',
  'REPAIR_STOCK_VARIANCE_NOTIFIED',
  'REPAIR_RETURN_REQUEST_CREATED',
  'REPAIR_POST_SEND_QUEUE_BYPASSED',
  'REPAIR_POST_SEND_FAILED'
]);

const TITLE_OVERRIDES: Record<string, string> = {
  CASE_CREATED: 'Case created',
  CASE_STATUS_UPDATED: 'Status updated',
  TASK_GROUP_ASSIGNED: 'Task group assigned',
  TASK_REASSIGNED: 'Assignment updated',
  CASE_SURVEY_RELEASED: 'Survey released',
  CASE_SURVEY_SUBMITTED: 'Survey submitted',
  TASK_CREATED: 'Task created',
  CASE_PROBLEM_UPDATED: 'Problem details updated',
  CASE_DOCUMENT_ATTACHED: 'Document attached',
  CASE_DEVICE_VERIFICATION_REQUIRED: 'Device verification required',
  CASE_DEVICE_VERIFIED: 'Device verified',
  CASE_DEVICE_INSTALL_DATES_UPDATED: 'Device install dates updated',
  TSFAD_ANALYSIS_ACCEPTED: 'Analysis accepted',
  SERVICE_REPORT_SHARED: 'Service report sent',
  REPAIR_CUSTOMER_MESSAGE_SENT: 'Customer notified',
  REPAIR_STOCK_CONSUMED: 'Repair stock consumed',
  REPAIR_STOCK_VARIANCE_NOTIFIED: 'Repair stock variance',
  REPAIR_RETURN_REQUEST_CREATED: 'Return request created',
  REPAIR_POST_SEND_QUEUE_BYPASSED: 'Post-send queue bypassed',
  REPAIR_POST_SEND_FAILED: 'Post-send processing failed',
  TSFAD_QUOTE_SAVED: 'Quote prepared',
  QUOTE_SHARED: 'Quote sent',
  QUOTE_INACTIVATED: 'Quote inactivated',
  QUOTE_REACTIVATED: 'Quote reactivated',
  QUOTE_ACCEPTED: 'Quote accepted',
  QUOTE_DECLINED: 'Quote declined',
  QUOTE_ADJUST_REQUESTED: 'Quote adjustment requested',
  VISIT_SCHEDULED: 'Visit scheduled',
  VISIT_RESCHEDULED: 'Visit rescheduled',
  VISIT_SCHEDULE_CONFIRMED: 'Visit confirmed',
  VISIT_COMPLETED: 'Visit completed',
  CASE_RESOLVED: 'Case resolved',
  CASE_CLOSED: 'Case closed'
};

const toUpper = (value?: string | null) => String(value || '').trim().toUpperCase();

const hasCustomerVisibilityFlag = (event: TimelineEventLike) => event.isCustomerVisible === true;

export const isTimelineEventVisible = (event: TimelineEventLike, audience: TimelineAudience) => {
  const type = toUpper(event.eventType);
  if (!type) return false;
  if (hasCustomerVisibilityFlag(event)) return true;
  if (CUSTOMER_EVENT_TYPES.has(type)) return true;
  if (audience === 'tech' && TECH_EXTRA_EVENT_TYPES.has(type)) return true;
  return false;
};

const formatStatus = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return 'Unknown';
  return value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(entry => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);
};

const resolveTaskReassignedNames = (metadata: Record<string, unknown>) => {
  const fromList = toStringList(metadata.assignedUserNames);
  return fromList.length > 0 ? fromList : [];
};

const resolveTaskReassignedIds = (metadata: Record<string, unknown>) => {
  const fromList = toStringList(metadata.assignedUserIds);
  return fromList.length > 0 ? fromList : [];
};

const formatSurveyScore = (scoreEarned: number | null, scorePossible: number | null) => {
  if (scoreEarned === null || scorePossible === null || scorePossible <= 0) return '';
  const percentage = Math.max(0, Math.min(100, Math.floor((scoreEarned / scorePossible) * 100)));
  return `${scoreEarned}/${scorePossible}, ${percentage}%`;
};

const getTaskReassignedSignature = (metadata: Record<string, unknown>) => {
  const names = resolveTaskReassignedNames(metadata);
  if (names.length > 0) return `NAMES:${names.join('|').toUpperCase()}`;
  const ids = resolveTaskReassignedIds(metadata);
  if (ids.length > 0) return `IDS:${ids.join('|').toUpperCase()}`;
  return 'UNASSIGNED';
};

const getTaskTimelineLabel = (metadata: Record<string, unknown>) =>
  (typeof metadata.taskLabel === 'string' && metadata.taskLabel.trim()) ||
  (typeof metadata.taskCode === 'string' && metadata.taskCode.trim()) ||
  'Visit';

const getTaskTimelineIdentity = (metadata: Record<string, unknown>) =>
  (
    (typeof metadata.taskId === 'string' && metadata.taskId.trim()) ||
    (typeof metadata.taskCode === 'string' && metadata.taskCode.trim()) ||
    (typeof metadata.taskLabel === 'string' && metadata.taskLabel.trim()) ||
    (typeof metadata.taskType === 'string' && metadata.taskType.trim()) ||
    'TASK'
  ).toUpperCase();

const getEventIdentityKey = (event: TimelineEventLike) =>
  String(event.id || `${toUpper(event.eventType)}-${event.occurredAt || ''}-${event.actorName || ''}`);

const getTimelineOccurredAtMs = (event: TimelineEventLike) => {
  const occurredAtMs = new Date(event.occurredAt || 0).getTime();
  return Number.isFinite(occurredAtMs) ? occurredAtMs : 0;
};

const getLatestGroupSignature = (event: TimelineEventLike) => {
  const type = toUpper(event.eventType);
  const metadata = toRecord(event.metadata);

  if (type === 'TSFAD_ANALYSIS_ACCEPTED') {
    return type;
  }
  if (type === 'TASK_GROUP_ASSIGNED') {
    return `${type}:${getTaskTimelineIdentity(metadata)}`;
  }
  if (type === 'TASK_REASSIGNED') {
    return `${type}:${getTaskTimelineIdentity(metadata)}`;
  }
  if (type === 'VISIT_SCHEDULED' || type === 'VISIT_RESCHEDULED' || type === 'VISIT_SCHEDULE_CONFIRMED') {
    return `VISIT_SCHEDULE:${getTaskTimelineIdentity(metadata)}`;
  }

  return null;
};

const getConsecutiveSignature = (event: TimelineEventLike) => {
  const type = toUpper(event.eventType);
  const metadata = toRecord(event.metadata);
  if (type === 'CASE_STATUS_UPDATED') {
    return `${type}:${String(metadata.status || '').toUpperCase()}`;
  }
  if (type === 'TASK_GROUP_ASSIGNED') {
    return `${type}:${String(metadata.taskType || metadata.taskCode || '').toUpperCase()}`;
  }
  if (type === 'TASK_REASSIGNED') {
    return `${type}:${getTaskReassignedSignature(metadata)}`;
  }
  if (type === 'TSFAD_QUOTE_SAVED') {
    const quote = toRecord(metadata.quote);
    return `${type}:${String(quote.quoteId || quote.title || 'QUOTE')}`;
  }
  if (type === 'SERVICE_REPORT_SHARED') {
    const report = toRecord(metadata.report);
    return `${type}:${String(report.reportId || metadata.reportId || report.title || metadata.title || 'REPORT')}`;
  }
  if (type === 'QUOTE_SHARED') {
    const quote = toRecord(metadata.quote);
    return `${type}:${String(quote.quoteId || metadata.quoteId || quote.title || metadata.title || 'QUOTE')}`;
  }
  if (
    type === 'REPAIR_CUSTOMER_MESSAGE_SENT' ||
    type === 'REPAIR_STOCK_CONSUMED' ||
    type === 'REPAIR_STOCK_VARIANCE_NOTIFIED' ||
    type === 'REPAIR_RETURN_REQUEST_CREATED' ||
    type === 'REPAIR_POST_SEND_QUEUE_BYPASSED' ||
    type === 'REPAIR_POST_SEND_FAILED'
  ) {
    const report = toRecord(metadata.report);
    return `${type}:${String(report.reportId || metadata.reportId || metadata.transferId || 'REPORT')}`;
  }
  if (type === 'VISIT_SCHEDULED' || type === 'VISIT_RESCHEDULED' || type === 'VISIT_SCHEDULE_CONFIRMED') {
    return `${type}:${String(metadata.taskId || metadata.taskCode || 'TASK')}:${String(metadata.plannedStart || '')}:${String(metadata.plannedDue || '')}`;
  }
  return type;
};

export const formatTimelineEventLabel = (event: TimelineEventLike) => {
  const type = toUpper(event.eventType);
  const metadata = toRecord(event.metadata);

  if (type === 'CASE_STATUS_UPDATED') {
    const from = formatStatus(metadata.previousStatus);
    const to = formatStatus(metadata.status);
    if (from === to) return `Status: ${to}`;
    return `Status: ${from} -> ${to}`;
  }

  if (type === 'TASK_REASSIGNED') {
    const assigneeNames = resolveTaskReassignedNames(metadata);
    const assigneeIds = resolveTaskReassignedIds(metadata);
    const assignee =
      (assigneeNames.length > 0 && assigneeNames.join(', ')) ||
      (assigneeIds.length > 0 && assigneeIds.join(', ')) ||
      'Unassigned';
    return `Assigned to ${assignee}`;
  }

  if (type === 'TASK_GROUP_ASSIGNED') {
    const taskType =
      (typeof metadata.taskType === 'string' && metadata.taskType.trim()) ||
      (typeof metadata.taskCode === 'string' && metadata.taskCode.trim()) ||
      'service task';
    return `Work assigned: ${taskType}`;
  }

  if (type === 'VISIT_SCHEDULED') {
    return `Visit scheduled: ${getTaskTimelineLabel(metadata)}`;
  }
  if (type === 'VISIT_RESCHEDULED') {
    return `Visit rescheduled: ${getTaskTimelineLabel(metadata)}`;
  }
  if (type === 'VISIT_SCHEDULE_CONFIRMED') {
    return `Visit confirmed: ${getTaskTimelineLabel(metadata)}`;
  }

  if (type === 'CASE_SURVEY_RELEASED') {
    const title =
      (typeof metadata.surveyName === 'string' && metadata.surveyName.trim()) ||
      'Customer satisfaction survey';
    return `Survey available: ${title}`;
  }
  if (type === 'CASE_SURVEY_SUBMITTED') {
    const title =
      (typeof metadata.surveyName === 'string' && metadata.surveyName.trim()) ||
      'Customer satisfaction survey';
    const scoreEarned =
      typeof metadata.scoreEarned === 'number' && Number.isFinite(metadata.scoreEarned)
        ? metadata.scoreEarned
        : null;
    const scorePossible =
      typeof metadata.scorePossible === 'number' && Number.isFinite(metadata.scorePossible)
        ? metadata.scorePossible
        : null;
    const scoreLabel = formatSurveyScore(scoreEarned, scorePossible);
    return scoreLabel
      ? `Survey submitted: ${title} (${scoreLabel})`
      : `Survey submitted: ${title}`;
  }

  if (type === 'TSFAD_QUOTE_SAVED') {
    const quote = toRecord(metadata.quote);
    const title =
      (typeof quote.title === 'string' && quote.title.trim()) ||
      (typeof metadata.title === 'string' && metadata.title.trim()) ||
      'Service quote';
    return `Quote prepared: ${title}`;
  }
  if (type === 'SERVICE_REPORT_SHARED') {
    const report = toRecord(metadata.report);
    const title =
      (typeof report.title === 'string' && report.title.trim()) ||
      (typeof metadata.title === 'string' && metadata.title.trim()) ||
      'Service report';
    return `Service report sent: ${title}`;
  }
  if (type === 'QUOTE_SHARED') {
    const quote = toRecord(metadata.quote);
    const title =
      (typeof quote.title === 'string' && quote.title.trim()) ||
      (typeof metadata.title === 'string' && metadata.title.trim()) ||
      'Service quote';
    return `Quote sent: ${title}`;
  }
  if (type === 'CASE_SURVEY_RELEASED' || type === 'CASE_SURVEY_SUBMITTED') {
    return `${type}:${String(metadata.surveyId || metadata.surveyName || 'SURVEY')}`;
  }
  if (type === 'REPAIR_CUSTOMER_MESSAGE_SENT') {
    const report = toRecord(metadata.report);
    const title =
      (typeof report.title === 'string' && report.title.trim()) ||
      (typeof metadata.title === 'string' && metadata.title.trim()) ||
      'Service report';
    return `Customer notified: ${title}`;
  }
  if (type === 'REPAIR_STOCK_CONSUMED') {
    const count =
      typeof metadata.consumedUsageCount === 'number' && Number.isFinite(metadata.consumedUsageCount)
        ? metadata.consumedUsageCount
        : null;
    return count ? `Repair stock consumed: ${count} part line${count === 1 ? '' : 's'}` : 'Repair stock consumed';
  }
  if (type === 'REPAIR_STOCK_VARIANCE_NOTIFIED') {
    const count =
      typeof metadata.issueCount === 'number' && Number.isFinite(metadata.issueCount)
        ? metadata.issueCount
        : null;
    return count ? `Repair stock variance flagged: ${count} issue${count === 1 ? '' : 's'}` : 'Repair stock variance flagged';
  }
  if (type === 'REPAIR_RETURN_REQUEST_CREATED') {
    const transferId =
      typeof metadata.transferId === 'string' && metadata.transferId.trim() ? metadata.transferId.trim() : null;
    return transferId ? `Return request created: ${transferId}` : 'Return request created';
  }
  if (type === 'REPAIR_POST_SEND_QUEUE_BYPASSED') {
    return 'Repair post-send queue bypassed';
  }
  if (type === 'REPAIR_POST_SEND_FAILED') {
    return 'Repair post-send processing failed';
  }
  if (type === 'CASE_DEVICE_INSTALL_DATES_UPDATED') {
    return 'Device install/commissioning dates updated';
  }

  if (TITLE_OVERRIDES[type]) return TITLE_OVERRIDES[type];
  if (!type) return 'Update logged';
  return type
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const buildMeaningfulTimeline = <T extends TimelineEventLike>(
  events: T[],
  audience: TimelineAudience
) => {
  const seen = new Set<string>();
  const visible = events
    .filter(event => isTimelineEventVisible(event, audience))
    .filter(event => {
      const key = getEventIdentityKey(event);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  const keptEventKeys = new Set<string>();
  const seenLatestGroups = new Set<string>();

  visible
    .slice()
    .sort((a, b) => getTimelineOccurredAtMs(b) - getTimelineOccurredAtMs(a))
    .forEach(event => {
      const latestGroup = getLatestGroupSignature(event);
      if (latestGroup) {
        if (seenLatestGroups.has(latestGroup)) return;
        seenLatestGroups.add(latestGroup);
      }
      keptEventKeys.add(getEventIdentityKey(event));
    });

  const semanticCollapsed = visible
    .filter(event => keptEventKeys.has(getEventIdentityKey(event)))
    .slice()
    .sort((a, b) => getTimelineOccurredAtMs(a) - getTimelineOccurredAtMs(b));
  const collapsed: T[] = [];
  semanticCollapsed.forEach(event => {
    const previous = collapsed[collapsed.length - 1];
    if (previous && getConsecutiveSignature(previous) === getConsecutiveSignature(event)) {
      collapsed[collapsed.length - 1] = event;
      return;
    }
    collapsed.push(event);
  });
  return collapsed;
};
